import React from "react";
import { Box } from "@mui/material";
import { getModelPreviewStyle } from "../../utils/modelPreview";
import MediaContent from "../ui/MediaContent";

function renderElement(el) {
    const base = {
        position: "absolute",
        left: el.x,
        top: el.y,
        width: el.width,
        height: el.height,
        zIndex: el.zIndex,
        boxSizing: "border-box",
    };

    if (el.type === "text") {
        return (
            <div key={el.id} style={base}>
                <span
                    style={{
                        fontSize: el.fontSize,
                        fontFamily: el.fontFamily,
                        fontWeight: el.fontWeight,
                        fontStyle: el.fontStyle,
                        color: el.color,
                        textAlign: el.align || "left",
                        display: "block",
                        width: "100%",
                        whiteSpace: "pre-wrap",
                    }}
                >
                    {el.text}
                </span>
            </div>
        );
    }

    if (el.type === "image") {
        return (
            <div key={el.id} style={{ ...base, overflow: "hidden", opacity: el.opacity ?? 1 }}>
                <MediaContent src={el.src} />
            </div>
        );
    }

    if (el.type === "rect") {
        return (
            <div
                key={el.id}
                style={{
                    ...base,
                    backgroundColor: el.noFill ? "transparent" : el.fill,
                    border: el.noBorder ? "none" : `${el.strokeWidth}px solid ${el.stroke}`,
                    borderRadius: el.borderRadius,
                    opacity: el.opacity,
                }}
            />
        );
    }

    if (el.type === "circle") {
        return (
            <div
                key={el.id}
                style={{
                    ...base,
                    backgroundColor: el.noFill ? "transparent" : el.fill,
                    border: el.noBorder ? "none" : `${el.strokeWidth}px solid ${el.stroke}`,
                    borderRadius: "50%",
                    opacity: el.opacity,
                }}
            />
        );
    }

    return null;
}

export default function ModelMiniature({ model, fit = false, contained = false }) {
    const containerRef = React.useRef(null);
    const [dims, setDims] = React.useState({ width: 280, height: null });

    React.useEffect(() => {
        const node = containerRef.current;
        if (!node) return;

        const ro = new ResizeObserver(([entry]) => {
            setDims({
                width: entry?.contentRect?.width || 280,
                height: contained ? (entry?.contentRect?.height || null) : null,
            });
        });

        ro.observe(node);
        return () => ro.disconnect();
    }, [contained]);

    const { canvasSize, scale, width: scaledW, height: scaledH } = getModelPreviewStyle(
        model,
        dims.width,
        { maxScale: (fit || contained) ? 1 : Infinity, containerHeight: dims.height }
    );
    const sorted = [...(model?.elements || [])].sort((a, b) => a.zIndex - b.zIndex);

    // contained mode: fills parent both ways, canvas centered and scaled to fit
    if (contained) {
        return (
            <Box
                ref={containerRef}
                sx={{
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                    borderRadius: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "#f5f7fa",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {/* Wrapper sized to the actual rendered dimensions so overflow clips cleanly */}
                <div style={{ width: scaledW, height: scaledH, position: "relative", flexShrink: 0, overflow: "hidden" }}>
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: canvasSize.width,
                            height: canvasSize.height,
                            backgroundColor: model?.canvasBg || "#ffffff",
                            transform: `scale(${scale})`,
                            transformOrigin: "top left",
                        }}
                    >
                        {sorted.map(renderElement)}
                    </div>
                </div>
            </Box>
        );
    }

    return (
        <Box
            ref={containerRef}
            sx={{
                width: "100%",
                height: scaledH,
                borderRadius: 1.5,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "#f5f7fa",
            }}
        >
            <div
                style={{
                    position: "relative",
                    width: canvasSize.width,
                    height: canvasSize.height,
                    backgroundColor: model?.canvasBg || "#ffffff",
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                }}
            >
                {sorted.map(renderElement)}
            </div>
        </Box>
    );
}
